import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, TextField, Chip, IconButton, Tooltip,
  ToggleButtonGroup, ToggleButton, Switch, FormControlLabel, Alert, Paper,
} from '@mui/material';
import {
  PlayArrow, Stop, DeleteOutlineOutlined, VerticalAlignBottom, FilterAlt,
} from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';

const MAX_LINES = 2000;

type LogLine = { id: number; source: string; line: string; ts: number; level: string };

const detectLevel = (line: string): string => {
  const u = line.toUpperCase();
  if (u.includes(' ERR') || u.includes('ERROR') || u.includes(' CRIT'))  return 'error';
  if (u.includes(' WARN') || u.includes('WARNING'))                        return 'warning';
  if (u.includes(' DEBUG'))                                                 return 'debug';
  return 'info';
};

const LINE_COLORS: Record<string, string> = {
  error:   '#ff6b6b',
  warning: '#ffd93d',
  debug:   '#888',
  info:    '#e8e8e8',
};

const SOURCE_COLORS: Record<string, string> = {
  freeswitch: '#4dabf7',
  kamailio:   '#69db7c',
  system:     '#aaa',
};

let idSeq = 0;

export const LogViewerPage: React.FC = () => {
  const [lines, setLines]         = useState<LogLine[]>([]);
  const [filter, setFilter]       = useState('');
  const [filterErr, setFilterErr] = useState('');
  const [source, setSource]       = useState<'freeswitch' | 'kamailio' | 'both'>('freeswitch');
  const [autoScroll, setAutoScroll] = useState(true);
  const [connected, setConnected] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const socketRef  = useRef<Socket | null>(null);
  const bottomRef  = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Validate filter regex
  useEffect(() => {
    if (!filter) { setFilterErr(''); return; }
    try { new RegExp(filter); setFilterErr(''); }
    catch (e: any) { setFilterErr(e.message); }
  }, [filter]);

  const filtered = React.useMemo(() => {
    if (!filter || filterErr) return lines;
    try {
      const re = new RegExp(filter, 'i');
      return lines.filter(l => re.test(l.line));
    } catch { return lines; }
  }, [lines, filter, filterErr]);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filtered, autoScroll]);

  // Detect manual scroll up → disable auto-scroll
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (!atBottom && autoScroll) setAutoScroll(false);
  }, [autoScroll]);

  const connect = useCallback(() => {
    if (socketRef.current) { socketRef.current.disconnect(); }

    const apiBase = (window as any).__API_BASE__ || '';
    const wsUrl   = apiBase ? apiBase.replace(/^http/, 'ws') : window.location.origin;
    const sock = io(`${wsUrl}/events`, {
      transports: ['websocket', 'polling'],
      path: '/socket.io',
      auth: { token: localStorage.getItem('access_token') || '' },  // server rejects unauthenticated sockets
    });
    sock.on('connect_error', () => { setConnected(false); setStreaming(false); });

    sock.on('connect',    () => { setConnected(true); });
    sock.on('disconnect', () => { setConnected(false); setStreaming(false); });

    sock.on('log:line', (data: { source: string; line: string; ts: number }) => {
      const entry: LogLine = {
        id:     ++idSeq,
        source: data.source || 'system',
        line:   data.line,
        ts:     data.ts,
        level:  detectLevel(data.line),
      };
      setLines(prev => {
        const next = [...prev, entry];
        return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
      });
    });

    socketRef.current = sock;
  }, []);

  const startStream = useCallback(() => {
    if (!socketRef.current?.connected) connect();
    setTimeout(() => {
      socketRef.current?.emit('logs:subscribe', { source });
      setStreaming(true);
    }, 300);
  }, [connect, source]);

  const stopStream = useCallback(() => {
    socketRef.current?.emit('logs:unsubscribe');
    setStreaming(false);
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    connect();
    return () => { socketRef.current?.disconnect(); };
  }, []);

  // Re-subscribe when source changes while streaming
  useEffect(() => {
    if (streaming && socketRef.current?.connected) {
      socketRef.current.emit('logs:subscribe', { source });
    }
  }, [source]);

  const clearLogs = () => setLines([]);

  const fmtTime = (ts: number) => new Date(ts).toLocaleTimeString('en-GB', { hour12: false });

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5" fontWeight={700}>Log Viewer</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
          <Chip
            size="small"
            label={connected ? 'WS Connected' : 'WS Disconnected'}
            color={connected ? 'success' : 'error'}
          />
          <Chip size="small" label={`${lines.length} lines`} />
        </Stack>
      </Stack>

      {/* Controls */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
        <ToggleButtonGroup
          size="small" exclusive
          value={source}
          onChange={(_, v) => v && setSource(v)}
        >
          <ToggleButton value="freeswitch" sx={{ fontSize: 11 }}>FreeSWITCH</ToggleButton>
          <ToggleButton value="kamailio"   sx={{ fontSize: 11 }}>Kamailio</ToggleButton>
          <ToggleButton value="both"       sx={{ fontSize: 11 }}>Both</ToggleButton>
        </ToggleButtonGroup>

        {!streaming
          ? <Tooltip title="Start stream"><IconButton color="success" onClick={startStream}><PlayArrow /></IconButton></Tooltip>
          : <Tooltip title="Stop stream"><IconButton color="error" onClick={stopStream}><Stop /></IconButton></Tooltip>
        }

        <TextField
          size="small"
          placeholder="Filter (regex)…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          error={!!filterErr}
          helperText={filterErr || undefined}
          InputProps={{ startAdornment: <FilterAlt fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
          sx={{ width: 280 }}
        />

        <FormControlLabel
          control={<Switch checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} size="small" />}
          label={<Typography variant="caption">Auto-scroll</Typography>}
        />

        <Tooltip title="Scroll to bottom">
          <IconButton size="small" onClick={() => { setAutoScroll(true); bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }}>
            <VerticalAlignBottom />
          </IconButton>
        </Tooltip>

        <Tooltip title="Clear log">
          <IconButton size="small" color="warning" onClick={clearLogs}><DeleteOutlineOutlined /></IconButton>
        </Tooltip>
      </Stack>

      {!connected && (
        <Alert severity="warning" sx={{ mb: 1 }} action={<IconButton size="small" onClick={connect}><PlayArrow fontSize="small"/></IconButton>}>
          WebSocket disconnected — click to reconnect, then Start.
        </Alert>
      )}

      {/* Terminal */}
      <Paper
        ref={containerRef}
        onScroll={handleScroll}
        elevation={0}
        sx={{
          flex: 1, minHeight: 400, maxHeight: 'calc(100vh - 280px)',
          overflowY: 'auto', bgcolor: '#1a1a2e', borderRadius: 2, p: 1.5,
          fontFamily: '"Cascadia Code", "Fira Code", "Courier New", monospace',
          fontSize: 12, lineHeight: 1.6,
        }}
      >
        {filtered.length === 0 && (
          <Typography sx={{ color: '#666', fontFamily: 'inherit', mt: 2, textAlign: 'center' }}>
            {streaming ? 'Waiting for log lines…' : 'Press ▶ to start streaming'}
          </Typography>
        )}
        {filtered.map(l => (
          <Box key={l.id} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Box component="span" sx={{ color: '#666', flexShrink: 0, fontSize: 10 }}>
              {fmtTime(l.ts)}
            </Box>
            {source === 'both' && (
              <Box component="span" sx={{ color: SOURCE_COLORS[l.source] || '#aaa', flexShrink: 0, fontSize: 10, width: 80 }}>
                [{l.source}]
              </Box>
            )}
            <Box component="span" sx={{ color: LINE_COLORS[l.level], whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {l.line}
            </Box>
          </Box>
        ))}
        <div ref={bottomRef} />
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        Showing {filtered.length} of {lines.length} lines · Max buffer: {MAX_LINES} lines
        {filter && !filterErr && ` · filter: /${filter}/i`}
      </Typography>
    </Box>
  );
};
